import React from 'react';
import { motion } from 'framer-motion';
import { Crown, Zap, ArrowRight } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { generateStudioRoomCode as _genCode, rememberRoom } from '@/lib/recentRooms';

/**
 * AdminFreeAccessBar
 * A large, impossible-to-miss red-gold banner shown at the very top of any paid
 * studio/live/practice page for admin users. Skips forms, payment, membership.
 *
 * Props:
 *   - title:     Bold headline (e.g. "Admin — Full Studio Access")
 *   - subtitle:  Short explainer (e.g. "One click to enter the room. No forms, no payment.")
 *   - primary:   { label, onClick, icon? } for the main action button (required)
 *   - secondary: { label, onClick, icon? } optional 2nd action button
 *   - testid:    data-testid for the outer container (required so we can assert per-page)
 */
const AdminFreeAccessBar = ({ title, subtitle, primary, secondary, testid }) => {
  const { isAdmin } = useAuth();
  const isAdminUser = typeof isAdmin === 'function' ? isAdmin() : false;
  if (!isAdminUser) return null;

  const PrimaryIcon = primary?.icon || Zap;
  const SecondaryIcon = secondary?.icon || ArrowRight;

  return (
    <motion.div
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      className="w-full sticky top-0 z-40 border-b-4 border-yellow-400"
      data-testid={testid || 'admin-free-access-bar'}
    >
      <div className="bg-gradient-to-r from-red-600 via-amber-500 to-yellow-500 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 py-3 flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4 justify-between">
          {/* Left — icon + title/subtitle */}
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-11 h-11 rounded-xl bg-black/25 text-yellow-100 flex items-center justify-center shrink-0 ring-2 ring-yellow-200/50">
              <Crown className="w-6 h-6" />
            </div>
            <div className="min-w-0">
              <p className="text-black font-black text-sm sm:text-base leading-tight uppercase tracking-wide">
                {title || 'Admin — Free Access'}
              </p>
              {subtitle && (
                <p className="text-black/75 text-xs sm:text-sm leading-tight mt-0.5">
                  {subtitle}
                </p>
              )}
            </div>
          </div>

          {/* Right — action buttons */}
          <div className="flex flex-wrap gap-2 shrink-0 w-full sm:w-auto">
            {primary && (
              <button
                type="button"
                onClick={primary.onClick}
                data-testid={`${testid || 'admin-free-access-bar'}-primary`}
                className="flex-1 sm:flex-none px-5 py-2.5 rounded-xl bg-black hover:bg-neutral-900 text-yellow-300 font-bold text-sm flex items-center justify-center gap-2 whitespace-nowrap transition-colors shadow-md"
              >
                <PrimaryIcon className="w-4 h-4" />
                {primary.label}
              </button>
            )}
            {secondary && (
              <button
                type="button"
                onClick={secondary.onClick}
                data-testid={`${testid || 'admin-free-access-bar'}-secondary`}
                className="flex-1 sm:flex-none px-5 py-2.5 rounded-xl bg-white/90 hover:bg-white text-black font-bold text-sm flex items-center justify-center gap-2 whitespace-nowrap transition-colors shadow-md"
              >
                <SecondaryIcon className="w-4 h-4" />
                {secondary.label}
              </button>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export const generateStudioRoomCode = () => {
  const code = _genCode();
  // Auto-remember any code created via the admin bar so it lands in Recent Rooms
  rememberRoom(code, { role: 'host' });
  return code;
};

export default AdminFreeAccessBar;
